import { on, off } from '../core/Events.js';
import { findFirstBySearchMask } from '../core/imcmd/SearchMask.js';
import { ImSys } from '../core/ImSys.js';
import { Widget } from '../core/Widget.js';
import { ViewWidgetHelper } from '../view/ViewWidgetHelper.js';
import { FilterWidget, signature } from './FilterWidget.js';
import { SearchUrlCodec } from './SearchUrlCodec.js';
import { PaginationWidget, PAGER_GROUP } from './PaginationWidget.js';
import { PresetRegistry } from './PresetRegistry.js';

/**
 * Виджет поиска
 */
class SearchWidget extends Widget {
    constructor() {
        super(...arguments);
        this._lastSig = null;
        /** Последняя сигнатура без значений pager (для сброса страницы при изменении фильтров). */
        this._lastCoreSig = null;
        /** Последний снимок значений фильтра (после apply/sync), key -> stable string */
        this._lastValueByKey = null;
    }
    /** Инициализация. */
    _create(options) {
        const ui = ImSys.tmp_id++;
        this._nsMask = ".search" + ui;
        this._options = options;
        // Определяем кодек
        const codecKey = options.codec ?? 'default';
        switch (codecKey) {
            // Кодек не используется
            case 'off':
                break;
            case 'default':
                this._codec = new SearchUrlCodec();
                break;
        }
        if (this._codec?.decode) {
            // init: URL → DOM фильтров (после того, как смонтируются остальные виджеты)
            queueMicrotask(() => this._syncFromUrl());
            // back/forward: URL → DOM фильтров
            on(window, "popstate" + this._nsMask, () => this._syncFromUrl());
        }
        on(this.element, "click" + this._nsMask, (e) => {
            const el = e.target;
            if (el?.closest('button')?.dataset.key == (this._options.buttonKey ?? "srchbutton")) {
                e.preventDefault();
                this.apply();
            }
        });
    }
    /** Контейнер для отображения выбранных фильтров (чипы) */
    get chipsHost() {
        if (!this._chipsHost) {
            const chipsMask = this._options?.chips;
            if (chipsMask) {
                this._chipsHost = findFirstBySearchMask(this.element, chipsMask);
            }
        }
        return this._chipsHost;
    }
    /** Контейнер для отображения количества выбранных фильтров. */
    get badgeHost() {
        if (!this._badgeHost) {
            const badgeMask = this._options?.badge;
            if (badgeMask) {
                this._badgeHost = findFirstBySearchMask(this.element, badgeMask);
            }
        }
        return this._badgeHost;
    }
    /**
     * Получение адаптера виджета
     */
    getFilterAdapter() {
        return this;
    }
    /** Type-guard: entry имеет value. */
    _hasValue(e) {
        return !!e.value;
    }
    /**
     * Применение изменений фильтра
     * Если изменились НЕ pager-поля — сбрасывает страницу к номеру 1 (default)
     */
    apply() {
        const entries = this._collectOwnedEntries();
        const a = this._calc(entries);
        // предотвращаем повторную обработку одного и того же набора фильтров
        if (a.sigAll === this._lastSig)
            return;
        // Если изменились "основные" параметры (не pager) — сбросить страницу
        if (this._lastCoreSig !== null && a.sigCore !== this._lastCoreSig) {
            const pagers = this._getOwnedPagersEnsured();
            for (const pw of pagers)
                pw.resetToDefault();
        }
        // Пересобираем подпись после возможного reset
        const nextEntries = this._collectOwnedEntries();
        const b = this._calc(nextEntries);
        if (b.sigAll === this._lastSig) {
            this._lastCoreSig = b.sigCore;
            return;
        }
        const changedKeys = this._getChangedKeys(nextEntries);
        const aggTargets = this._getAggReloadTargets(nextEntries, changedKeys);
        // обновим снимок уже сейчас — чтобы последующие вызовы не считали это же изменение повторно
        this._lastValueByKey = this._buildValueSnapshot(nextEntries);
        this._lastSig = b.sigAll;
        this._lastCoreSig = b.sigCore;
        // Синхронизируем состояние url
        const mode = this._codec?.sync(nextEntries);
        // Если произошел redirect, останавливаем обработку
        if (mode === 'redirect')
            return;
        this._applyUrlStateToDom();
        // Обновляем зависимые элементы
        this._reloadDependentElements(aggTargets);
        // Обновить общий флаг "фильтры включены"
        this._syncFiltersOnFlag();
    }
    /**
     * Обновление зависимых элементов.
     * Например - результаты поиска, информационные панели и проч.
     */
    _reloadDependentElements(aggTargets) {
        if (aggTargets?.length > 0) {
            const filters = this._getOwnedFiltersEnsured();
            for (const fw of filters) {
                fw.reloadFields(aggTargets);
            }
        }
        if (this._options.reload) {
            // Выполняем команду обновления
            ViewWidgetHelper.execCmdString(this.element, this.element, this._options.reload);
        }
    }
    /** Собрать записи фильтра как есть из DOM (без пресетов). */
    _collectOwnedEntriesRaw() {
        const map = new Map();
        const filters = this._getOwnedFiltersEnsured();
        for (const fw of filters) {
            for (const e of fw.dom.collectEntries()) {
                const key = e.schema.key;
                if (!map.has(key))
                    map.set(key, e);
            }
        }
        const pagers = this._getOwnedPagersEnsured();
        for (const pw of pagers) {
            for (const e of pw.collectEntries()) {
                const key = e.schema.key;
                if (!map.has(key))
                    map.set(key, e);
            }
        }
        return Array.from(map.values());
    }
    /** Эффективные записи (raw + применённые пресеты). */
    _collectOwnedEntries() {
        const raw = this._collectOwnedEntriesRaw();
        return PresetRegistry.default.apply(raw);
    }
    /** Получить owned FilterWidget, гарантируя инициализацию descendants в DOM. */
    _getOwnedFiltersEnsured() {
        const hosts = this.element.querySelectorAll('[data-widget~="Filter"]');
        hosts.forEach((host) => {
            FilterWidget.getOrInit(host);
        });
        return Widget.getAll(FilterWidget).filter(fw => fw.ownerHost === this.element);
    }
    /** Получить owned PaginationWidget, гарантируя инициализацию descendants в DOM. */
    _getOwnedPagersEnsured() {
        const hosts = this.element.querySelectorAll('[data-widget~="Pagination"]');
        hosts.forEach((host) => {
            PaginationWidget.getOrInit(host);
        });
        return Widget.getAll(PaginationWidget).filter(pw => pw.ownerHost === this.element);
    }
    /**
     * Синхронизация: URL → DOM фильтров в scope данного SearchWidget.
     * Не делает commit в history.
     */
    _syncFromUrl() {
        const codec = this._codec;
        if (!codec?.decode)
            return;
        const isInit = this._lastSig === null;
        const entries = this._collectOwnedEntriesRaw();
        const state = codec.decode(location.search, entries);
        // Расшифровыеваем специфические параметры зависимые от решения
        PresetRegistry.default.syncFromUrl(entries, state);
        const filters = this._getOwnedFiltersEnsured();
        for (const fw of filters)
            fw.applyExternalState(state);
        const pagers = this._getOwnedPagersEnsured();
        for (const pw of pagers)
            pw.applyExternalState(state);
        // ✅ DOM уже восстановлен, теперь берём effective entries и prime по ним
        const effective = this._collectOwnedEntries();
        codec.primeFromSearch?.(location.search, effective);
        const c = this._calc(effective);
        this._syncFiltersOnFlag();
        // первый sync — просто зафиксировать состояние
        if (isInit) {
            this._lastSig = c.sigAll;
            this._lastCoreSig = c.sigCore;
            return;
        }
        if (c.sigAll === this._lastSig)
            return;
        const changedKeys = this._getChangedKeys(effective);
        const aggTargets = this._getAggReloadTargets(effective, changedKeys);
        this._lastValueByKey = this._buildValueSnapshot(effective);
        this._lastSig = c.sigAll;
        this._lastCoreSig = c.sigCore;
        this._reloadDependentElements(aggTargets);
    }
    /** Активные значения и сигнатуры (все и без pager). */
    _calc(entries) {
        const allValues = [];
        const coreValues = [];
        for (const e of entries) {
            if (!this._hasValue(e))
                continue;
            allValues.push(e.value);
            if (e.schema.group !== PAGER_GROUP) {
                coreValues.push(e.value);
            }
        }
        return {
            allValues,
            coreValues,
            sigAll: signature(allValues),
            sigCore: signature(coreValues)
        };
    }
    /** Получить элемент для переключения класса (через search mask). */
    get filtersFlagHost() {
        if (this._filtersFlagHost !== undefined)
            return this._filtersFlagHost;
        const mask = this._options?.filtersonmask?.trim();
        if (!mask) {
            // маски нет → фича отключена
            this._filtersFlagHost = null;
            return null;
        }
        this._filtersFlagHost = findFirstBySearchMask(this.element, mask) ?? null;
        return this._filtersFlagHost;
    }
    /** Сколько активных фильтров (как для badge), но только core (без pager). */
    _coreBadgeCount(entries) {
        let n = 0;
        for (const e of entries) {
            if (!e.value || e.value == e.schema.default)
                continue;
            if (e.schema.group === PAGER_GROUP)
                continue; // pager не считаем
            if (e.schema.showInBadge === false)
                continue; // как в бейдже
            n++;
        }
        return n;
    }
    /** Обновить класс "фильтры включены" на целевом элементе. */
    _syncFiltersOnFlag() {
        const host = this.filtersFlagHost;
        if (!host)
            return;
        const effective = this._collectOwnedEntries();
        const count = this._coreBadgeCount(effective);
        const cls = (this._options.filtersonclass ?? "filters-on").trim() || "filters-on";
        host.classList.toggle(cls, count > 0);
    }
    /**
     * Стабильное представление значения для сравнения.
     * Важно: сравниваем именно value (а не title), чтобы не дергать reload на косметике.
     */
    _toStableValue(e) {
        const v = (this._hasValue(e) ? e.value.value : "");
        return `${e.schema.key}=${v}`;
    }
    /**
     * Построить снимок значений по ключам для diff.
     */
    _buildValueSnapshot(entries) {
        const m = new Map();
        for (const e of entries) {
            const val = this._hasValue(e) ? e.value.value : "";
            m.set(e.schema.key, val ?? "");
        }
        return m;
    }
    /**
     * Получить список ключей, которые изменились относительно последнего состояния.
     * Если снимка нет (первый запуск) — вернёт пустой список, чтобы не делать лишних reload.
     */
    _getChangedKeys(nextEntries) {
        const prev = this._lastValueByKey;
        if (!prev)
            return [];
        const next = this._buildValueSnapshot(nextEntries);
        const out = [];
        // compare keys present in next
        for (const [k, nextVal] of next) {
            const prevVal = prev.get(k) ?? "";
            if (prevVal !== nextVal)
                out.push(k);
        }
        // compare keys that vanished (если такое возможно)
        for (const [k, prevVal] of prev) {
            if (!next.has(k) && (prevVal ?? "") !== "")
                out.push(k);
        }
        return out;
    }
    /**
     * Вычислить аггр-поля, которые нужно перезагрузить после изменения фильтров.
     *
     * Правила:
     * - Перезагружаем только поля с schema.isAgg === true.
     * - Любое изменение влияет на агрегации, если у триггера не стоит schema.noAggEffect === true.
     * - Если в текущем изменении участвовало аггр-поле — оно исключается из targets
     *   (не перезагружаем то, что пользователь менял руками).
     */
    _getAggReloadTargets(entries, changedKeys) {
        if (changedKeys.length === 0)
            return [];
        const byKey = new Map();
        const aggKeys = [];
        for (const e of entries) {
            byKey.set(e.schema.key, e);
            if (e.schema.isAgg)
                aggKeys.push(e.schema.key);
        }
        if (aggKeys.length === 0)
            return [];
        // Если все изменения помечены noAggEffect — аггр не трогаем
        let hasAggEffect = false;
        for (const k of changedKeys) {
            const e = byKey.get(k);
            // ✅ pager не влияет на агрегации
            if (e?.schema.group === PAGER_GROUP)
                continue;
            // по умолчанию влияет, кроме явного noAggEffect
            if (!e || e.schema.noAggEffect !== true) {
                hasAggEffect = true;
                break;
            }
        }
        if (!hasAggEffect)
            return [];
        const changedAgg = new Set();
        for (const k of changedKeys) {
            const e = byKey.get(k);
            if (e?.schema.isAgg)
                changedAgg.add(k);
        }
        return aggKeys.filter(k => !changedAgg.has(k));
    }
    //** Обратное восстановление состояния элементов при изменениях */
    _applyUrlStateToDom() {
        const codec = this._codec;
        if (!codec?.decode)
            return;
        const scope = this._collectOwnedEntriesRaw();
        const state = codec.decode(location.search, scope);
        PresetRegistry.default.syncFromUrl(scope, state);
        for (const fw of this._getOwnedFiltersEnsured())
            fw.applyExternalState(state);
        for (const pw of this._getOwnedPagersEnsured())
            pw.applyExternalState(state);
    }
    _onDestroy() {
        // снимет все popstate.searchN на window
        off(window, this._nsMask);
        // Отписываемся от прослушивания кнопки и текстового поля
        off(this.element, this._nsMask);
    }
}
/** Имя виджета */
SearchWidget._alias = "Search";

export { SearchWidget };
